/**
 * REST data provider for orchestra-api.
 *
 * All CRUD operations map to orchestra-api REST endpoints.
 * Authorization: Bearer <idToken> is injected on every request.
 * On 401, GET and HEAD requests may refresh the Cognito session and retry once.
 * Requests with any other method are never replayed automatically.
 * Sub-path resources (e.g. 'score_tables/generate') are passed through
 * verbatim so existing UI calls work without any changes.
 */

import type { DataProvider } from 'react-admin';
import {
  getIdToken,
  checkCredentials,
  dispatch403,
  expireSessionIfCurrent,
  getSessionGeneration,
  getSessionRevision,
  isSessionChangedError,
  isSessionExpiredError,
  sessionChangedError,
} from './cognito';
import { PARTICIPATION_RETIRED_CODE, PARTICIPATION_RETIRED_MESSAGE } from '../utils/participation-retirement';
import { getListFilter, getListSort } from './list-utils';

const baseUrl = (): string =>
  (import.meta.env.VITE_API_BASE_URL || '').replace(/\/$/, '');

/**
 * Convert a react-admin resource name to an API path segment.
 * Replaces underscores with hyphens in each path segment.
 * Examples:
 *   score_tables            -> score-tables
 *   student_groups          -> student-groups
 *   score_tables/generate   -> score-tables/generate
 *   score_tables/abc123/approve -> score-tables/abc123/approve
 */
export const resourceToPath = (resource: string): string =>
  resource
    .split('/')
    .map((seg) => seg.replace(/_/g, '-'))
    .join('/');

function makeHeaders(token: string, requested?: HeadersInit): HeadersInit {
  const headers = new Headers(requested);
  headers.set('Content-Type', 'application/json');
  headers.set('Authorization', `Bearer ${token}`);
  return headers;
}

export function mapApiError(status: number, raw: string): Error {
  let serverMsg: string | undefined;
  let code: string | undefined;
  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    if (typeof parsed['error'] === 'string') serverMsg = parsed['error'];
    if (!serverMsg && Array.isArray(parsed['errors'])) {
      serverMsg = parsed['errors']
        .filter((item: any) => typeof item?.message === 'string')
        .map((item: any) => `${typeof item.path === 'string' ? `${item.path}: ` : ''}${item.message}`)
        .join('; ') || undefined;
    }
    if (typeof parsed['code'] === 'string') code = parsed['code'];
  } catch { /* not JSON */ }

  let message: string;
  if (status === 409 && code === PARTICIPATION_RETIRED_CODE) {
    message = PARTICIPATION_RETIRED_MESSAGE;
  } else if (status === 401) {
    message = 'Your session has expired — please sign in again.';
  } else if (status === 403) {
    message = serverMsg ?? "You don't have permission to do that.";
  } else if (status === 404) {
    message = serverMsg ?? 'That record no longer exists.';
  } else if ((status === 400 || status === 422) && serverMsg) {
    message = serverMsg;
  } else {
    console.error(`API error ${status}:`, raw);
    message = serverMsg ?? 'Something went wrong. The details have been logged.';
  }

  return Object.assign(new Error(message), { status, ...(code ? { code } : {}) });
}

function networkError(): Error {
  return Object.assign(new Error("Can't reach the server — check your connection."), { status: 0 });
}

export async function apiFetch(path: string, options: RequestInit = {}, pinnedToken?: string) {
  const method = (options.method ?? 'GET').toUpperCase();
  const allowsAuthenticationRetry = method === 'GET' || method === 'HEAD';
  const revision = getSessionRevision();
  const initialToken = getIdToken();
  if (!initialToken) throw Object.assign(new Error('Sign in to access this API'), { status: 401 });
  const configuredBase = baseUrl();
  if (!configuredBase) throw new Error('The authorized API endpoint is not configured');
  const url = `${configuredBase}/${path}`;
  const operationKey = new Headers(options.headers).get('Idempotency-Key');
  if (operationKey !== null && !/^[A-Za-z0-9][A-Za-z0-9._:-]{15,127}$/.test(operationKey)) {
    throw new Error('Invalid idempotency key');
  }
  if (pinnedToken !== undefined && getIdToken() !== pinnedToken) {
    throw sessionChangedError('Capture session changed; sign in as the original actor to retry');
  }

  let requestToken = pinnedToken ?? initialToken;
  let authorizationGeneration = getSessionGeneration();
  const assertAuthorizationCurrent = (message: string) => {
    if (
      getSessionRevision() !== revision ||
      getSessionGeneration() !== authorizationGeneration ||
      getIdToken() !== requestToken
    ) throw sessionChangedError(message);
  };

  let res: Response;
  try {
    res = await fetch(url, { ...options, cache: 'no-store', credentials: 'omit', referrerPolicy: 'no-referrer',
      signal: options.signal ?? (pinnedToken ? AbortSignal.timeout(30000) : undefined),
      headers: makeHeaders(requestToken, options.headers),
    });
  } catch {
    assertAuthorizationCurrent('Session changed; request failed after the session was replaced');
    throw networkError();
  }
  assertAuthorizationCurrent('Session changed; response discarded');

  if (res.status === 401 && pinnedToken === undefined && allowsAuthenticationRetry) {
    // Refresh the Cognito session token and retry once.
    const tokenBeforeRefresh = requestToken;
    const generationBeforeRefresh = authorizationGeneration;
    let refreshedToken: string | void;
    try {
      refreshedToken = await checkCredentials();
    } catch (error) {
      if (isSessionChangedError(error)) throw error;
      if (isSessionExpiredError(error)) throw mapApiError(401, '');
      if (
        getSessionRevision() !== revision ||
        getSessionGeneration() !== generationBeforeRefresh ||
        getIdToken() !== tokenBeforeRefresh
      ) {
        throw sessionChangedError('Session changed; request was not replayed');
      }
      expireSessionIfCurrent(revision, generationBeforeRefresh, tokenBeforeRefresh);
      throw mapApiError(401, '');
    }
    if (typeof refreshedToken !== 'string' || !refreshedToken) {
      throw mapApiError(401, '');
    }
    const expectedGeneration = generationBeforeRefresh +
      (refreshedToken === tokenBeforeRefresh ? 0 : 1);
    if (
      getSessionRevision() !== revision ||
      getSessionGeneration() !== expectedGeneration ||
      getIdToken() !== refreshedToken
    ) {
      throw sessionChangedError('Session changed; request was not replayed');
    }
    requestToken = refreshedToken;
    authorizationGeneration = expectedGeneration;
    try {
      res = await fetch(url, { ...options, cache: 'no-store', credentials: 'omit', referrerPolicy: 'no-referrer', headers: makeHeaders(refreshedToken, options.headers) });
    } catch {
      assertAuthorizationCurrent('Session changed; replay failed after the session was replaced');
      throw networkError();
    }
  }

  assertAuthorizationCurrent('Session changed; response discarded');
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    assertAuthorizationCurrent('Session changed; response discarded');
    if (
      res.status === 401 &&
      !expireSessionIfCurrent(revision, authorizationGeneration, requestToken)
    ) {
      if (
        getSessionRevision() !== revision ||
        getSessionGeneration() !== authorizationGeneration
      ) {
        throw sessionChangedError('Session changed; terminal authentication failure discarded');
      }
    }
    if (
      res.status === 401 &&
      (text.toLowerCase().includes('tenantid') || text.toLowerCase().includes('missing tenant'))
    ) {
      throw Object.assign(
        new Error(
          'Missing tenantId claim in authentication token. ' +
            'Contact your administrator to ensure your account is provisioned.',
        ),
        { status: 401 },
      );
    }
    const error = mapApiError(res.status, text);
    if (res.status === 403) {
      const authorizationOperation = options.signal ?? {};
      Object.assign(error, {
        authorizationSessionRevision: revision,
        authorizationSessionGeneration: authorizationGeneration,
        authorizationOperation,
      });
      dispatch403(revision, authorizationGeneration, authorizationOperation);
    }
    throw error;
  }

  if (res.status === 204) return null;
  let data;
  try {
    data = await res.json();
  } catch (error) {
    assertAuthorizationCurrent('Session changed; response body discarded');
    throw error;
  }
  assertAuthorizationCurrent('Session changed; response discarded');
  return data;
}

function buildQuery(filter: Record<string, unknown>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filter)) {
    if (value === undefined) continue;
    if (typeof value === 'string' || typeof value === 'number') {
      params.set(key, String(value));
    }
  }
  const q = params.toString();
  return q ? `?${q}` : '';
}

type RestRecord = Record<string, unknown>;

/**
 * React Admin requires every record to have a stable `id`. These identifiers
 * are the identifiers implemented by the corresponding API routes; this map is
 * deliberately explicit so storage keys never become an accidental fallback.
 */
const REST_ID_FIELDS: Readonly<Record<string, string>> = {
  courses: 'id',
  instances: 'id',
  student_groups: 'id',
  surveys: 'id',
  assignments: 'id',
  rankings: 'id',
  instructors: 'userId',
  programs: 'id',
  events: 'id',
  score_tables: 'id',
  attempts: 'id',
  students: 'id',
  assessments: 'id',
  tallies: 'id',
  users: 'id',
  role_assignments: 'sk',
  licenses: 'licenseId',
  teams: 'id',
  wearable_consents: 'student_id',
  biometrics: 'sk',
  mos_profiles: 'id',
};

const rootResource = (resource: string): string => resource.split('/')[0];

export function normaliseRestRecord(resource: string, value: unknown): RestRecord {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error(`Invalid ${resource} API record`);
  }
  const record = value as RestRecord;
  const field = REST_ID_FIELDS[rootResource(resource)];
  if (!field) throw new Error(`No React Admin identity contract for resource: ${resource}`);
  const identifier = record[field];
  if ((typeof identifier !== 'string' && typeof identifier !== 'number') || identifier === '') {
    throw new Error(`${resource} API record is missing its ${field} identifier`);
  }
  return field === 'id' ? record : { ...record, id: identifier };
}

const normaliseRestRecords = (resource: string, items: unknown[]): RestRecord[] =>
  items.map(item => normaliseRestRecord(resource, item));

const CURSOR_RESOURCES = new Set(['attempts', 'students']);
const CURSOR_PAGE_SIZE = 500;
const MAX_CURSOR_PAGES = 200;

async function readCompleteCursorList(
  resource: string,
  filter: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<RestRecord[]> {
  const path = resourceToPath(resource);
  const seen = new Set<string>();
  const items: RestRecord[] = [];
  let cursor: string | undefined;

  for (let page = 0; page < MAX_CURSOR_PAGES; page += 1) {
    const query = new URLSearchParams({ limit: String(CURSOR_PAGE_SIZE) });
    if (typeof filter.q === 'string' || typeof filter.q === 'number') query.set('q', String(filter.q));
    if (cursor) query.set('cursor', cursor);
    const raw = await apiFetch(`${path}?${query.toString()}`, { signal });
    if (!raw || typeof raw !== 'object' || !Array.isArray(raw.items)) {
      throw new Error(`${resource} API returned an invalid cursor page`);
    }
    items.push(...normaliseRestRecords(resource, raw.items));
    if (raw.next_cursor === undefined) return items;
    if (typeof raw.next_cursor !== 'string' || !raw.next_cursor || seen.has(raw.next_cursor)) {
      throw new Error(`${resource} API returned an invalid or repeated cursor`);
    }
    seen.add(raw.next_cursor);
    cursor = raw.next_cursor;
  }

  throw new Error(`${resource} list exceeds the bounded cursor traversal limit`);
}

async function readCompleteList(
  resource: string,
  filter: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<RestRecord[]> {
  if (CURSOR_RESOURCES.has(rootResource(resource))) {
    return readCompleteCursorList(resource, filter, signal);
  }
  const path = resourceToPath(resource);
  const raw = await apiFetch(`${path}${buildQuery(filter)}`, { signal });
  const items: unknown[] = Array.isArray(raw) ? raw : (raw?.items ?? []);
  if (!Array.isArray(items)) throw new Error(`${resource} API returned an invalid list`);
  return normaliseRestRecords(resource, items);
}

const pick = (data: RestRecord, fields: readonly string[]): RestRecord =>
  Object.fromEntries(fields.filter(field => data[field] !== undefined).map(field => [field, data[field]]));

const STUDENT_UPDATE_FIELDS = [
  'id', 'orgPath', 'expected_version', 'archived_at', 'assignment_id', 'course_id',
  'first_name', 'last_name', 'middle_initial', 'person_id', 'attributes', 'student_group_id',
] as const;

export function projectRestUpdate(resource: string, data: RestRecord): RestRecord {
  const root = rootResource(resource);
  if (root === 'rankings') return pick(data, ['name', 'archived_at']);
  if (root === 'students') return pick(data, STUDENT_UPDATE_FIELDS);
  if (root === 'attempts') return pick(data, ['notes', 'expected_version', 'correction_reason']);

  const idField = REST_ID_FIELDS[root];
  if (idField && idField !== 'id') {
    const body = { ...data };
    delete body.id;
    return body;
  }
  return data;
}

function projectRestCreate(resource: string, data: RestRecord): RestRecord {
  const root = rootResource(resource);
  if (root === 'rankings') return pick(data, ['name', 'archived_at']);
  if (root === 'students') return pick(data, STUDENT_UPDATE_FIELDS);
  const idField = REST_ID_FIELDS[root];
  if (idField && idField !== 'id') {
    const body = { ...data };
    delete body.id;
    return body;
  }
  return data;
}

function normaliseCreateResult(resource: string, value: unknown): RestRecord {
  if (rootResource(resource) === 'attempts' && value && typeof value === 'object' && !Array.isArray(value)) {
    const response = value as Record<string, unknown>;
    const hasTeamWrapper = Object.hasOwn(response, 'team_attempt_group') || Object.hasOwn(response, 'attempts');
    if (hasTeamWrapper) {
      if (
        typeof response.team_attempt_group !== 'string' ||
        !response.team_attempt_group ||
        !Array.isArray(response.attempts)
      ) {
        throw new Error('attempts API returned an invalid accepted-team wrapper');
      }
      return {
        ...response,
        attempts: normaliseRestRecords('attempts', response.attempts),
      };
    }
  }
  return normaliseRestRecord(resource, value);
}

export const createRestProvider = () => ({
  getList: async (resource, params) => {
    const {
      sort = { field: 'id', order: 'ASC' },
      filter = {},
      pagination = { page: 1, perPage: 25 },
    } = params || {};
    const { page = 1, perPage = 25 } = pagination;

    const allItems = await readCompleteList(resource, filter, params?.signal);

    const filtered = allItems.filter(getListFilter(filter));
    const sorted = filtered.sort(getListSort(sort));
    const paged = sorted.slice((page - 1) * perPage, page * perPage);

    return { data: paged, total: filtered.length };
  },

  getOne: async (resource, params) => {
    const path = resourceToPath(resource);
    const data = normaliseRestRecord(resource, await apiFetch(
      `${path}/${encodeURIComponent(String(params.id))}`,
      { signal: params.signal },
    ));
    return { data };
  },

  getMany: async (resource, params) => {
    const path = resourceToPath(resource);
    // A missing/denied reference is not a complete roster. Surface the error.
    const items = await Promise.all(params.ids.map(async id =>
      normaliseRestRecord(resource, await apiFetch(
        `${path}/${encodeURIComponent(String(id))}`,
        { signal: params.signal },
      ))));
    return { data: items };
  },

  getManyReference: async (resource, params) => {
    const { page = 1, perPage = 25 } = params.pagination || {};
    const filter = { ...params.filter, [params.target]: params.id };
    const allItems = await readCompleteList(resource, filter, params.signal);
    const filtered = allItems.filter(getListFilter(filter));
    const sorted = filtered.sort(getListSort(params.sort));
    const paged = sorted.slice((page - 1) * perPage, page * perPage);
    return { data: paged, total: filtered.length };
  },

  create: async (resource, params) => {
    // Sub-path resources (e.g. score_tables/generate, score_tables/{id}/approve)
    // are passed through verbatim — only underscores are converted to hyphens.
    const path = resourceToPath(resource);
    const requestData = projectRestCreate(resource, params.data);
    const data = await apiFetch(path, {
      method: 'POST',
      body: JSON.stringify(requestData),
    });
    return { data: normaliseCreateResult(resource, data ?? requestData) };
  },

  update: async (resource, params) => {
    const path = resourceToPath(resource);
    const requestData = projectRestUpdate(resource, params.data);
    const data = await apiFetch(`${path}/${encodeURIComponent(String(params.id))}`, {
      method: 'PUT',
      body: JSON.stringify(requestData),
    });
    const fallback = { ...(params.previousData ?? {}), ...requestData };
    return { data: normaliseRestRecord(resource, data ?? fallback) };
  },

  delete: async (resource, params) => {
    if (resource === 'attempts') throw new Error('Accepted attempts cannot be deleted');
    if (resource === 'attempts') throw new Error('Accepted attempts cannot be deleted');
    const path = resourceToPath(resource);
    await apiFetch(`${path}/${encodeURIComponent(String(params.id))}`, { method: 'DELETE' });
    return { data: { id: params.id } };
  },

  updateMany: async (resource, params) => {
    if (resource === 'attempts') throw new Error('Review and annotate each accepted attempt individually');
    if (resource === 'attempts') throw new Error('Review and annotate each accepted attempt individually');
    const path = resourceToPath(resource);
    const requestData = projectRestUpdate(resource, params.data);
    await Promise.all(
      params.ids.map((id) =>
        apiFetch(`${path}/${encodeURIComponent(String(id))}`, {
          method: 'PUT',
          body: JSON.stringify(requestData),
        }),
      ),
    );
    return { data: params.ids };
  },

  deleteMany: async (resource, params) => {
    if (resource === 'attempts') throw new Error('Accepted attempts cannot be deleted');
    if (resource === 'attempts') throw new Error('Accepted attempts cannot be deleted');
    const path = resourceToPath(resource);
    await Promise.all(
      params.ids.map((id) => apiFetch(`${path}/${encodeURIComponent(String(id))}`, { method: 'DELETE' })),
    );
    return { data: params.ids };
  },
}) as DataProvider;
